import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import TM from "@/components/TM";
import { useRole } from "@/contexts/RoleContext";
import { useGrade } from "@/contexts/GradeContext";

const API = process.env.REACT_APP_BACKEND_URL;

// 20 static stars — upper 50% of screen. No animation.
const STUDENT_STARS = [
  { left: "6%",  top: "4%",  size: 1,   opacity: 0.50 },
  { left: "14%", top: "10%", size: 1.5, opacity: 0.40 },
  { left: "22%", top: "6%",  size: 1,   opacity: 0.60 },
  { left: "31%", top: "14%", size: 1,   opacity: 0.35 },
  { left: "43%", top: "3%",  size: 1.5, opacity: 0.50 },
  { left: "55%", top: "9%",  size: 1,   opacity: 0.45 },
  { left: "65%", top: "16%", size: 1.5, opacity: 0.55 },
  { left: "74%", top: "5%",  size: 1,   opacity: 0.40 },
  { left: "83%", top: "20%", size: 1.5, opacity: 0.60 },
  { left: "92%", top: "11%", size: 1,   opacity: 0.35 },
  { left: "4%",  top: "26%", size: 1,   opacity: 0.45 },
  { left: "17%", top: "32%", size: 1.5, opacity: 0.30 },
  { left: "28%", top: "38%", size: 1,   opacity: 0.55 },
  { left: "39%", top: "22%", size: 1.5, opacity: 0.40 },
  { left: "51%", top: "44%", size: 1,   opacity: 0.35 },
  { left: "63%", top: "29%", size: 1,   opacity: 0.50 },
  { left: "76%", top: "37%", size: 1.5, opacity: 0.45 },
  { left: "85%", top: "47%", size: 1,   opacity: 0.30 },
  { left: "9%",  top: "48%", size: 1,   opacity: 0.40 },
  { left: "48%", top: "15%", size: 1,   opacity: 0.50 },
];

export default function StudentLogin() {
  const navigate = useNavigate();
  const { setLearner } = useRole();
  const { setSelectedGrade } = useGrade();
  const [learnerId, setLearnerId] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API}/api/auth/student/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          learner_id: learnerId.trim(), 
          pin: pin.trim() 
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Invalid learner ID or PIN");
      }

      // Store token and learner info
      sessionStorage.setItem("student_token", data.token);
      sessionStorage.setItem("learner_id", data.learner_id);
      sessionStorage.setItem("learner_name", data.name || "");
      sessionStorage.setItem("learner_grade", String(data.grade || 5));
      if (data.teacher_name) sessionStorage.setItem("teacher_name", data.teacher_name);
      else sessionStorage.removeItem("teacher_name");
      if (data.school_subject_whitelist)
        sessionStorage.setItem("school_subject_whitelist", JSON.stringify(data.school_subject_whitelist));
      else
        sessionStorage.removeItem("school_subject_whitelist");
      sessionStorage.setItem("grade_subject_grid_active", String(data.grade_subject_grid_active || false));
      if (data.grade_subject_grid && Object.keys(data.grade_subject_grid).length > 0)
        sessionStorage.setItem("grade_subject_grid", JSON.stringify(data.grade_subject_grid));
      else
        sessionStorage.removeItem("grade_subject_grid");

      // Sync React context so pages don't need a hard reload
      const grade = parseInt(data.grade || 5, 10);
      setLearner({
        learner_id: data.learner_id,
        name: data.name || "Student",
        grade,
      });
      setSelectedGrade(grade);

      // Show the welcome screen before landing on the dashboard
      navigate("/welcome-screen", {
        state: {
          is_first_login: data.is_first_login || false,
          name: data.name || "friend",
        },
      });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden"
      style={{ background: "radial-gradient(ellipse 100% 20% at 50% 105%, rgba(210, 100, 30, 0.35) 0%, transparent 100%), linear-gradient(to bottom, #0d1b2a 0%, #0f1d30 40%, #111c2e 100%)" }}
      data-testid="student-login-page"
    >
      {/* Static stars */}
      {STUDENT_STARS.map((s, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "#ffffff",
            opacity: s.opacity,
            pointerEvents: "none",
          }}
        />
      ))}
      <div className="w-full max-w-md relative z-10">
        <div
          className="rounded-3xl p-8 border-0 mit-shadow bg-white"
        >
          {/* Icon + title */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5 bg-sky-100"
            >
              <BookOpen size={32} className="text-sky-500" />
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-800 tracking-tight">
              Student Login
            </h1>
            <p className="text-sm mt-2 text-slate-600">
              Welcome to Mitraa<TM />
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="learner-id"
                className="block text-sm font-semibold mb-2 text-slate-700"
              >
                Learner ID
              </label>
              <input
                id="learner-id"
                type="text"
                autoComplete="username"
                required
                value={learnerId}
                onChange={(e) => setLearnerId(e.target.value)}
                placeholder="Enter your learner ID"
                className="w-full px-4 py-3 rounded-xl text-sm text-slate-800 placeholder-slate-400 border border-slate-200 outline-none transition-all focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                data-testid="student-login-learner-id"
              />
            </div>

            <div>
              <label
                htmlFor="pin"
                className="block text-sm font-semibold mb-2 text-slate-700"
              >
                PIN
              </label>
              <input
                id="pin"
                type="password"
                autoComplete="current-password"
                required
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter your 4-digit PIN"
                className="w-full px-4 py-3 rounded-xl text-sm text-slate-800 placeholder-slate-400 border border-slate-200 outline-none transition-all focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                data-testid="student-login-pin"
              />
            </div>

            {error && (
              <div
                className="text-sm px-4 py-3 rounded-xl bg-red-50 text-red-600 border border-red-100"
                data-testid="student-login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-sky-500 hover:bg-sky-600 text-white transition-all mt-2"
              data-testid="student-login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            {/* Back link — inside card */}
            <div className="border-t border-slate-100 pt-5 mt-2 text-center space-y-3">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="back-to-welcome-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Welcome
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => navigate("/try")}
                  className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                  data-testid="try-mitraa-entry-btn"
                >
                  Try Mitraa — no login needed
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-6" style={{ color: "rgba(255,255,255,0.60)" }}>
          A product of AdiUtthan, powered by Adiora<TM /> Engine
        </p>
      </div>
    </div>
  );
}
